/**
 * TicketHandoffCard — cartão de HANDOFF de abertura de chamado.
 *
 * O Atendente montou um rascunho (title/description/priority/request_type e,
 * após a triagem guiada, tenant_id/category_id CONFIRMADOS — category_hint fica
 * como fallback por nome). Este card NÃO cria o chamado: leva o usuário à tela
 * real (/chamados/novo) com os campos pré-preenchidos, onde ele revisa, completa
 * os campos obrigatórios (incl. PCI) e confirma. Toda a validação acontece na tela.
 *
 * O draft repassa TODOS os params via spread (`...params`) — sem allowlist de
 * campos — então tenant_id/category_id seguem para o NovoChamado, que pré-seleciona
 * o tenant e a categoria de forma determinística (por ID).
 */
import { Badge, Button, Card, Group, Text } from '@mantine/core'
import { IconAlertTriangle, IconInfoCircle, IconTicket } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'

interface Props {
  params: Record<string, string>
}

const ROTULO_TIPO: Record<string, string> = {
  incident: 'Incidente',
  service_request: 'Requisição',
  change_request: 'Mudança',
}

const ROTULO_PRIORIDADE: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
}

const COR_PRIORIDADE: Record<string, string> = {
  low: 'gray',
  medium: 'blue',
  high: 'orange',
  critical: 'red',
}

export function TicketHandoffCard({ params }: Props) {
  const navigate = useNavigate()

  // Desde docs/CHAT_TRIAGEM_CHAMADO_SPEC.md (RF-001) o @Atendente propõe o rascunho
  // no MESMO turno, sem interrogatório de cinco perguntas. Então este cartão passou a
  // ser o PRIMEIRO lugar onde a pessoa vê o que vai ser criado — e precisa mostrar,
  // não só o título. Nada é criado aqui: a revisão e a validação (incl. PCI) seguem
  // acontecendo na tela de destino.
  const tenants = useAuthStore((s) => s.user)?.tenants ?? []
  const tenantId = Number(params.tenant_id)
  const tenantNome = Number.isFinite(tenantId)
    ? tenants.find((t) => t.id === tenantId)?.name
    : undefined
  const categoria = params.category_hint?.trim()
  const tipo = ROTULO_TIPO[params.request_type ?? ''] ?? params.request_type
  const prioridade = params.priority ?? ''

  // TASK-11810 (Fatia 0, Task 6/7): `_maybe_fill_resource_id` pode ter trocado
  // o `resource_id` do rascunho pelo alvo da âncora do alerta desta conversa,
  // ou avisado que o id que o humano ESCREVEU diverge do alerta (sem
  // corrigir — o id do humano vence). Os dois campos não são string, ao
  // contrário do resto do rascunho (título, prioridade, tenant_id como
  // texto) — por isso o cast local, no mesmo padrão de
  // `hostOrderPlanoFromParams`/`terraformDiffFromParams` em
  // ActionProposalCard.tsx, em vez de reescrever o tipo de `params` inteiro.
  const bruto = params as unknown as Record<string, unknown>
  const alvoCorrigidoDe =
    typeof bruto.alvo_corrigido_de === 'string' ? bruto.alvo_corrigido_de : undefined
  const alvoDivergeDoAlerta = bruto.alvo_diverge_do_alerta as
    | { alerta: string; proposta: string }
    | undefined

  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      style={{ borderLeft: '3px solid var(--mantine-color-teal-6, #0d9488)', maxWidth: 480 }}
    >
      <Group gap="xs" mb="xs" wrap="nowrap">
        <IconTicket size={16} />
        <Text size="sm" fw={600} style={{ flex: 1 }}>
          {`Abrir chamado: ${params.title ?? '?'}`}
        </Text>
      </Group>
      {(tenantNome || tipo || categoria || prioridade) && (
        <Group gap={6} mb="xs" wrap="wrap">
          {tenantNome && (
            <Badge size="sm" variant="light" color="gray">
              {tenantNome}
            </Badge>
          )}
          {tipo && (
            <Badge size="sm" variant="light" color="gray">
              {tipo}
            </Badge>
          )}
          {categoria && (
            <Badge size="sm" variant="light" color="gray">
              {categoria}
            </Badge>
          )}
          {prioridade && (
            <Badge size="sm" variant="light" color={COR_PRIORIDADE[prioridade] ?? 'gray'}>
              {ROTULO_PRIORIDADE[prioridade] ?? prioridade}
            </Badge>
          )}
        </Group>
      )}
      {alvoCorrigidoDe && (
        // Correção SILENCIOSA no servidor (o modelo chutou um id, a âncora do
        // alerta venceu) — mas silenciosa não pode virar invisível: mostrar o
        // ANTES e o DEPOIS é o que permite ao usuário conferir a troca antes
        // de confirmar o chamado. Cor info (`inovareTeal`, design system) por
        // ser resultado já resolvido, não um alerta sobre o estado atual.
        <Group gap={6} mb="xs" wrap="nowrap" align="flex-start" data-alvo-corrigido="1">
          <IconInfoCircle
            size={14}
            color="var(--mantine-color-inovareTeal-6)"
            style={{ marginTop: 2 }}
          />
          <Text size="xs" c="dimmed">
            O alvo deste chamado foi corrigido de{' '}
            <Text span size="xs" c="dimmed" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
              {alvoCorrigidoDe}
            </Text>{' '}
            para{' '}
            <Text span size="xs" c="dimmed" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
              {params.resource_id}
            </Text>{' '}
            — o alvo do alerta desta conversa.
          </Text>
        </Group>
      )}
      {alvoDivergeDoAlerta && (
        // Aqui NADA foi corrigido: o humano escreveu o alvo no turno, e ele
        // vence — mas a divergência com o alerta fica registrada como AVISO
        // (cor `yellow`, mesma semântica de `suspeito`/`contradicao_com_alerta`
        // no laudo), para o usuário confirmar que é mesmo essa a máquina.
        <Group
          gap={6}
          mb="xs"
          wrap="nowrap"
          align="flex-start"
          data-alvo-diverge="1"
          role="alert"
        >
          <IconAlertTriangle
            size={14}
            color="var(--mantine-color-yellow-6)"
            style={{ marginTop: 2 }}
          />
          <Text size="xs">
            O alerta desta conversa aponta para{' '}
            <Text span size="xs" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
              {alvoDivergeDoAlerta.alerta}
            </Text>
            , mas o chamado foi aberto para{' '}
            <Text span size="xs" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
              {alvoDivergeDoAlerta.proposta}
            </Text>{' '}
            (o que você escreveu).
          </Text>
        </Group>
      )}
      <Text size="xs" c="dimmed" mb="xs">
        Você será levado à tela de abertura com os campos preenchidos para revisar e confirmar.
      </Text>
      <Button
        size="xs"
        color="inovareTeal"
        variant="filled"
        leftSection={<IconTicket size={14} />}
        onClick={() =>
          // Proveniência: marca o draft como assistido pelo @Atendente para o
          // chamado registrar "criado por Fulano + @Atendente" (origin=ia_assistant).
          navigate('/chamados/novo', { state: { draft: { ...params, assisted_by_ia: '1' } } })
        }
      >
        Revisar e abrir chamado
      </Button>
    </Card>
  )
}
